import { Space, Table, Tooltip } from 'antd';
import { DeleteActionSvg, EditActionSvg } from 'utils/svgs';
import styles from './index.module.scss';
import { useContext } from 'react';
import { PermissionContext } from 'components/Global/AuthLayout';
import { useSelector } from 'react-redux';
import { formatNumber, invalidText } from 'utils/utils';
import { InfoCircleOutlined } from '@ant-design/icons';
import Ellipse from 'components/Global/Ellipse';
import { earningsFormulaMapping } from 'constants/Data';

const { Column } = Table;

type Props = {
	earningData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	fetchRecords: any;
	setEditData: any;
	openDrawerHandler: any;
	setDrawerInfoHandler: any;
	showModal: any;
	isLoading: boolean;
};
const FIXED_EARNINGS = [
	'BASIC PAY',
	'HOUSING ALLOWANCE',
	'SUNDAY OVERTIME',
	'WEEKDAY OVERTIME',
	'LEAVE PAY',
	'SHIFT DIFFERENCE PAY',
	'GRATUITY PAY',
	'LEAVE DEDUCTION',
];

const EarningTable = (props: Props) => {
	const {
		earningData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		openDrawerHandler,
		setDrawerInfoHandler,
		setEditData,
		showModal,
		isLoading,
	} = props;

	const permissions = useContext(PermissionContext);
	const { data } = useSelector((state: any) => state?.userProfile);
	// Edit user data handler
	const editDataHandler = (selectedObj: any) => {
		openDrawerHandler();
		setDrawerInfoHandler('Edit Earning');
		setEditData(selectedObj);
	};

	// Delete user data handler
	const deleteDataHandler = (userObject: any) => {
		setEditData(userObject);
		showModal();
	};
	return (
		<div className={styles['dynamic-table']}>
			<Table
				dataSource={earningData}
				scroll={{ y: 'calc(100vh - 360px)' }}
				pagination={{
					total: totalRecords,
					current: currentPage,
					pageSize: pageSize,
					showSizeChanger: false,
					// onShowSizeChange: pageSizeHandler,
					pageSizeOptions: [10, 20, 50, 100, 200],
				}}
				onChange={tableChangeHandler}
				rowKey={(record) => record.id}
				loading={isLoading}
			>
				<Column
					title="Earning Code"
					dataIndex="codeInt"
					key="codeInt"
					sorter={true}
					className="bg-white"
				/>

				<Column
					title="Earning"
					dataIndex="displayName"
					key="displayName"
					className="bg-white"
					sorter={true}
					render={(text) =>
						invalidText(text) ? (
							'-'
						) : (
							<Ellipse
								message={text}
								maxLength={20}
								key={text}
								tooltipMessage={text}
								isTooltip={true}
							/>
						)
					}
				/>
				<Column
					title={
						<span>
							<b>Amount</b>{' '}
							<Tooltip title="The amount is in respective employee's currency">
								<InfoCircleOutlined
									style={{
										marginLeft: 4,
										fontSize: '14px',
										color: '#584495',
									}}
								/>
							</Tooltip>
						</span>
					}
					dataIndex="amount"
					key="amount"
					className="bg-white"
					render={(value: string) => {
						return invalidText(value)
							? '-'
							: formatNumber(Number(value), data?.CompanyCurrencies);
					}}
				/>

				<Column
					title={
						<span>
							<b>Formula</b>{' '}
							<Tooltip
								title={
									<ul>
										{Object.entries(earningsFormulaMapping).map(
											([key, value]) => (
												<ul key={key}>
													<span>{key}:</span> {value}
												</ul>
											),
										)}
									</ul>
								}
								placement="bottomLeft"
							>
								<InfoCircleOutlined
									style={{
										marginLeft: 4,
										fontSize: '14px',
										color: '#584495',
									}}
								/>
							</Tooltip>
						</span>
					}
					dataIndex="formula"
					key="formula"
					className="bg-white"
					render={(text) => (invalidText(text) ? '-' : <span>{text}</span>)}
				/>

				{(permissions.allowedPermissions.includes('Edit_Earnings_Code') ||
					permissions.allowedPermissions.includes('Delete_Earnings_Code')) && (
					<Column
						title="Action"
						key="action"
						className="bg-white"
						render={(_: any, row: any) => {
							const isFixed = FIXED_EARNINGS.includes(row?.description || '');

							return (
								<Space size={20}>
									{permissions.allowedPermissions.includes(
										'Edit_Earnings_Code',
									) ? (
										!isFixed ? (
											<div
												className="cursor-pointer flex align-center justify-center"
												onClick={() => editDataHandler(row)}
											>
												<EditActionSvg />
											</div>
										) : (
											<Tooltip title="This default earning cannot be edited ">
												<div className="cursor-not-allowed flex align-center justify-center">
													<EditActionSvg color="#928F8F" />
												</div>
											</Tooltip>
										)
									) : (
										<div className="cursor-not-allowed flex align-center justify-center">
											<EditActionSvg color="#928F8F" />
										</div>
									)}

									{permissions.allowedPermissions.includes(
										'Delete_Earnings_Code',
									) ? (
										!isFixed ? (
											<div
												className="cursor-pointer flex align-center justify-center"
												onClick={() => deleteDataHandler(row)}
											>
												<DeleteActionSvg />
											</div>
										) : (
											<Tooltip title="This default earning cannot be deleted.">
												<div className="cursor-not-allowed flex align-center justify-center">
													<DeleteActionSvg color="#928F8F" />
												</div>
											</Tooltip>
										)
									) : (
										<div className="cursor-not-allowed flex align-center justify-center">
											<DeleteActionSvg color="#928F8F" />
										</div>
									)}
								</Space>
							);
						}}
					/>
				)}
			</Table>
		</div>
	);
};

export default EarningTable;
